import { useState } from "react"

import { usePostPortfolioAnalyticsHomegroupFiltersEngaged } from "src/data/analytics/queries/portfolioAnalyticsAnalyticsQueries"
import {
  useFetchHomeGroupCount,
  useFetchHomeGroups,
} from "src/data/homeGroups/queries/homeGroupQueries"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useUrlParam } from "src/hooks/useUrlParam"
import { langKeys } from "src/i18n/langKeys"
import { useTranslate } from "src/i18n/useTranslate"
import { DropdownMultiSelect } from "src/ui/DropdownSelect/DropdownMultiSelect"

export function PortfolioAnalyticsHomegroupFilter() {
  const { org } = useOrganization()
  const { t } = useTranslate()
  const orgId = org.id

  const { value: homegroupIds, replaceValue } = useUrlParam("homegroup_ids")
  const [searchText, setSearchText] = useState("")

  const { data: totalHomeGroups } = useFetchHomeGroupCount({ orgId })

  const fetchHomeGroups = useFetchHomeGroups({
    orgId,
    filter: {
      name: searchText || undefined,
      limit: totalHomeGroups,
    },
    options: { keepPreviousData: true },
  })

  const homegroups = fetchHomeGroups.data?.homegroups || []

  const groupedOptions = [
    {
      title: t(langKeys.home_group_building_plural),
      options: homegroups
        .filter((hg) => hg.type === "building")
        .map((hg) => ({ value: hg.id, label: hg.name })),
    },
    {
      title: t(langKeys.organizations_home_group_plural),
      options: homegroups
        .filter((hg) => hg.type === "area")
        .map((hg) => ({ value: hg.id, label: hg.name })),
    },
  ]

  const selectedValues = homegroupIds ? homegroupIds.split(",") : []

  const postHomegroupFiltersEngaged =
    usePostPortfolioAnalyticsHomegroupFiltersEngaged()

  function handleChange({
    checked,
    option,
  }: {
    checked: boolean
    option: { value: string }
  }) {
    let newValues: string[]
    if (checked) {
      newValues = [...selectedValues, option.value]
      postHomegroupFiltersEngaged.mutate({ homeGroupId: option.value })
    } else {
      newValues = selectedValues.filter((id) => id !== option.value)
    }
    replaceValue(newValues.length ? newValues.join(",") : undefined)
  }

  function handleClearSelection() {
    replaceValue(undefined)
    setSearchText("")
  }

  return (
    <DropdownMultiSelect
      label={`${t(langKeys.building)}/${t(langKeys.organizations_home_group)}`}
      options={groupedOptions}
      selectedValues={selectedValues}
      onChange={handleChange}
      onSearch={setSearchText}
      onClearSelection={handleClearSelection}
      loading={fetchHomeGroups.isLoading}
      placeholder={t(langKeys.search)}
    />
  )
}
